import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import {
  POSTER_PAGE,
  POSTER_PHOTO_LIMIT,
  POSTER_PHOTO_MAX_BYTES,
  type PosterDoc,
  type PosterElement,
  type PosterImageSource,
  type PosterShapeKind,
  type PosterStateResponse,
  type PosterStickerKind,
  type PosterTextElement,
  POSTER_SHAPE_ASPECT,
  POSTER_SLOT_NAME,
} from "@studio/shared";
import { InlineSpinner } from "../../components/InlineSpinner";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../../components/StudioShell";
import { ApiError } from "../../lib/api";
import { POSTER_BACKGROUNDS, POSTER_COLORS, SHAPE_NAMES, STICKER_NAMES, STICKER_PATHS } from "../../poster/art";
import { savePosterDoc, uploadPosterPhoto, usePoster } from "../../poster/api";
import {
  addElement,
  duplicateElement,
  History,
  imageElement,
  removeElement,
  reorderElement,
  resizeElement,
  shapeElement,
  snapRotation,
  stickerElement,
  textElement,
  updateElement,
  type OrderAction,
  type TextPreset,
} from "../../poster/editorModel";
import { cssWeight, loadPosterFonts, missingGlyphs, POSTER_FONTS, posterFont } from "../../poster/fonts";
import { canvasMeasure, layoutText, MM_PER_PT, type TextLayout } from "../../poster/geometry";
import { PosterElementView } from "../../poster/PosterElementView";
import { imageResolverFor } from "../../poster/PosterPreview";
import { PosterSaver, type PosterSaveState } from "../../poster/saver";
import { goPosterStep } from "../../poster/steps";
import { AUTO_QR, withoutQr } from "../../poster/autoQr";
import { starterDoc } from "../../poster/template";
import { PosterDownloadDialog } from "./PosterDownloadDialog";
import { PosterResumeGate } from "./PosterResumeGate";

/**
 * 포스터 2단계 「편집하기」(D4 · 목업 s2) — 패키지 없이 직접 만든 A3 편집기.
 *
 * 🔑 좌표는 mm, 그릴 때만 px 로 바꾼다. 글상자 줄은 `layoutText` 한 번으로 정해 편집 화면·PDF 가 같이 쓴다.
 * 🔑 저장은 PosterSaver(D17) — 2초 쉬면 · 최대 5초 · 한 번에 하나 · 직전 revision.
 * 🔑 QR 은 편집 화면에 없다 — 그리는 쪽이 오른쪽 아래 구석에 자동으로 붙인다(poster/autoQr.ts · 2026-10-04).
 *    여기서는 그 자리만 흐리게 보여 준다(중요한 글을 그 밑에 두지 않게).
 */

type Tool = "image" | "text" | "shape" | "sticker" | "bg";

const TOOLS: Array<{ key: Tool; label: string; icon: ReactNode }> = [
  { key: "image", label: "그림", icon: <path d="M4 5h16v14H4zM4 15l4-4 4 4 3-3 5 5M15 9.5a1.5 1.5 0 1 1 0-.01" /> },
  { key: "text", label: "글상자", icon: <path d="M5 6h14M12 6v13M9 19h6" /> },
  { key: "shape", label: "도형", icon: <path d="M4 4h8v8H4zM15 13a4 4 0 1 1 0 .01" /> },
  { key: "sticker", label: "스티커", icon: <path d="M12 3l2.6 5.6 6 .7-4.5 4.1 1.3 6L12 16.4 6.6 19.4l1.3-6L3.4 9.3l6-.7z" /> },
  { key: "bg", label: "배경색", icon: <path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" /> },
];

const ELEMENT_KIND_LABEL: Record<PosterElement["type"], string> = {
  text: "글상자", image: "그림", shape: "도형", sticker: "스티커", qr: "QR 코드",
};

const SAVE_LABEL: Record<PosterSaveState, string> = {
  idle: "자동 저장됨",
  pending: "고치는 중",
  saving: "저장 중…",
  saved: "자동 저장됨 · 방금",
  error: "저장 연결 끊김 · 다시 보내는 중",
  conflict: "다른 곳에서 고쳤어요",
};

function isTyping(): boolean {
  const active = document.activeElement;
  return Boolean(active && /^(INPUT|TEXTAREA|SELECT)$/u.test(active.tagName));
}

interface Drag {
  mode: "move" | "se" | "rot";
  id: string;
  startX: number;
  startY: number;
  origin: PosterElement;
  before: PosterDoc;
  centerX: number;
  centerY: number;
}

export function PosterEditPage({ workId }: { workId: string }) {
  const poster = usePoster(workId);
  if (poster.loading) return <LoadingPage label="포스터를 불러오는 중..." />;
  if (!poster.data) return <ErrorPage message={poster.error?.message ?? "포스터를 불러오지 못했어요."} />;
  return (
    <PosterResumeGate state={poster.data}>
      <PosterEditor workId={workId} state={poster.data} refresh={poster.refresh} />
    </PosterResumeGate>
  );
}

function PosterEditor({
  workId,
  state,
  refresh,
}: {
  workId: string;
  state: PosterStateResponse;
  refresh: () => Promise<PosterStateResponse | null>;
}) {
  const [doc, setDoc] = useState<PosterDoc>(() => withoutQr(state.doc ?? starterDoc(state)));
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool>("text");
  const [saveState, setSaveState] = useState<PosterSaveState>(state.doc ? "idle" : "pending");
  const [toast, setToast] = useState<string | null>(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [, setTick] = useState(0);
  const history = useRef(new History());
  const drag = useRef<Drag | null>(null);
  const lastDown = useRef<{ id: string | null; at: number }>({ id: null, at: 0 });
  const stageRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const textEditor = useRef<HTMLTextAreaElement | null>(null);
  const editBefore = useRef<PosterDoc | null>(null);
  const docRef = useRef(doc);
  docRef.current = doc;

  const saver = useMemo(() => new PosterSaver(state.revision, {
    save: (next, base) => savePosterDoc(workId, next, base),
    isConflict: (error) => error instanceof ApiError && error.code === "STALE_REVISION",
    // 4xx 는 다시 보내도 같다(시간 초과 408·요청 과다 429 제외) — 자동 재시도로 서버를 두드리지 않는다.
    isPermanent: (error) => error instanceof ApiError && error.status >= 400 && error.status < 500
      && error.status !== 408 && error.status !== 429,
    onState: (next) => setSaveState(next),
  }), [workId, state.revision]);
  // 화면이 내려가면(앱 안 뒤로가기 포함) 남은 편집을 바로 보낸다 — dispose 는 그것을 버렸다(검수 QA-03).
  useEffect(() => {
    saver.open();
    return () => saver.close();
  }, [saver]);
  // 처음 들어온 포스터(문서 없음)는 바로 저장해 둔다 — 교사 진행판이 빈 칸을 보이지 않게.
  useEffect(() => {
    if (!state.doc) saver.update(docRef.current);
  }, [saver, state.doc]);

  useEffect(() => {
    void loadPosterFonts().then(() => setFontsReady(true));
  }, []);

  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(() => setStageSize({ width: node.clientWidth, height: node.clientHeight }));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // 사진이 올라가거나 그림이 새로 그려지면 주소가 바뀐다 — 문서는 그대로, 그림 주소만 새로 읽는다.
  const [media, setMedia] = useState(state);
  // 그림 생성 중 폴링(usePoster)이 새 state 를 주면 그림 주소를 따라 바꾼다(2026-10-05 검수 QA-04 —
  // 예전에는 처음 state 로만 채워 편집 중 완성된 그림이 나타나지 않았다). 방금 올린 사진이
  // 폴링 응답보다 먼저 들어왔을 수 있어 사진은 합친다.
  useEffect(() => {
    setMedia((current) => current === state ? current : {
      ...state,
      photos: [
        ...state.photos,
        ...current.photos.filter((photo) => !state.photos.some((known) => known.asset_id === photo.asset_id)),
      ],
    });
  }, [state]);
  const resolveImage = useMemo(() => imageResolverFor(media), [media]);

  const scale = Math.max(0.5, Math.min(
    (stageSize.width - 60) / POSTER_PAGE.width,
    (stageSize.height - 70) / POSTER_PAGE.height,
  ));

  const layouts = useMemo(() => {
    const map = new Map<string, TextLayout>();
    for (const element of doc.els) if (element.type === "text") map.set(element.id, layoutText(element, canvasMeasure));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, fontsReady]);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((current) => current === message ? null : current), 2_400);
  }, []);

  /** 문서를 바꾼다. history=true 면 되돌리기 칸을 하나 남긴다. */
  const change = useCallback((next: PosterDoc, before: PosterDoc | null = docRef.current) => {
    if (before && JSON.stringify(before) !== JSON.stringify(next)) history.current.push(before);
    docRef.current = next;
    setDoc(next);
    saver.update(next);
    setTick((value) => value + 1);
  }, [saver]);

  const selectedElement = doc.els.find((element) => element.id === selected) ?? null;

  const finishTextEdit = useCallback(() => {
    if (!editing) return;
    const value = textEditor.current?.value ?? "";
    const before = editBefore.current;
    editBefore.current = null;
    setEditing(null);
    const current = docRef.current;
    const target = current.els.find((element) => element.id === editing);
    if (!target || target.type !== "text" || target.text === value) return;
    change(updateElement(current, editing, { text: value }), before ?? current);
  }, [change, editing]);

  const add = (element: PosterElement) => {
    finishTextEdit();
    change(addElement(docRef.current, element));
    setSelected(element.id);
  };

  const remove = () => {
    if (!selected) return;
    const result = removeElement(docRef.current, selected);
    if (!result.removed) return;
    change(result.doc);
    setSelected(null);
  };

  const undo = () => {
    finishTextEdit();
    const previous = history.current.undo(docRef.current);
    if (!previous) return;
    docRef.current = previous;
    setDoc(previous);
    saver.update(previous);
    setSelected(null);
  };
  const redo = () => {
    finishTextEdit();
    const next = history.current.redo(docRef.current);
    if (!next) return;
    docRef.current = next;
    setDoc(next);
    saver.update(next);
    setSelected(null);
  };

  // 키보드 — 글 고치는 중엔 끼어들지 않는다.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (editing) {
        if (event.key === "Escape") finishTextEdit();
        return;
      }
      if (isTyping()) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y") {
        event.preventDefault();
        redo();
        return;
      }
      if (!selected) return;
      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        remove();
        return;
      }
      if (event.key === "Escape") {
        setSelected(null);
        return;
      }
      const step = event.shiftKey ? 10 : 1;
      const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[event.key];
      if (delta) {
        event.preventDefault();
        const element = docRef.current.els.find((candidate) => candidate.id === selected);
        if (element) change(updateElement(docRef.current, selected, { x: element.x + delta[0]!, y: element.y + delta[1]! }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // 화면을 닫기 전 — 남은 것을 보낸다(브라우저가 기다려 주지는 않지만 대부분 나간다).
  useEffect(() => {
    const onHide = (): void => void saver.flushNow().catch(() => undefined);
    window.addEventListener("pagehide", onHide);
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (saver.hasUnsaved()) {
        void saver.flushNow().catch(() => undefined);
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [saver]);

  /* ---------- 끌기 · 크기 · 회전 ---------- */
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const handle = (event.target as HTMLElement).dataset.handle as Drag["mode"] | undefined;
    const node = (event.target as HTMLElement).closest<HTMLElement>("[data-id]");
    if (!node) {
      finishTextEdit();
      setSelected(null);
      return;
    }
    const id = node.dataset.id!;
    if (editing === id) return;
    finishTextEdit();
    const element = docRef.current.els.find((candidate) => candidate.id === id);
    if (!element) return;
    // 🔑 두 번 누르기는 여기서 센다 — 끌기 때문에 포인터를 붙잡으면 dblclick 이 요소가 아니라
    //    포스터 판으로 가서(포인터 캡처) 글 고치기가 열리지 않는다.
    const now = Date.now();
    if (element.type === "text" && !handle && lastDown.current.id === id && now - lastDown.current.at < 450) {
      lastDown.current = { id: null, at: 0 };
      editBefore.current = docRef.current;
      setSelected(id);
      setEditing(id);
      event.preventDefault();
      return;
    }
    lastDown.current = { id, at: now };
    setSelected(id);
    if (element.type !== "qr") setTool(element.type);
    const page = pageRef.current!.getBoundingClientRect();
    const height = element.type === "text" ? layouts.get(id)?.height ?? 10 : element.h;
    drag.current = {
      mode: handle ?? "move",
      id,
      startX: event.clientX,
      startY: event.clientY,
      origin: structuredClone(element),
      before: docRef.current,
      centerX: page.left + (element.x + element.w / 2) * scale,
      centerY: page.top + (element.y + height / 2) * scale,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current) return;
    const dx = (event.clientX - current.startX) / scale;
    const dy = (event.clientY - current.startY) / scale;
    let next: PosterElement;
    if (current.mode === "move") {
      next = { ...current.origin, x: Math.round((current.origin.x + dx) * 2) / 2, y: Math.round((current.origin.y + dy) * 2) / 2 };
    } else if (current.mode === "se") {
      next = resizeElement(current.origin, dx, dy, event.shiftKey, current.origin);
    } else {
      if (current.origin.type === "qr") return;
      const angle = Math.atan2(event.clientY - current.centerY, event.clientX - current.centerX) * 180 / Math.PI + 90;
      next = { ...current.origin, rot: snapRotation(angle, event.shiftKey) };
    }
    const updated = updateElement(docRef.current, current.id, next);
    docRef.current = updated;
    setDoc(updated);
  };

  const onPointerUp = () => {
    const current = drag.current;
    drag.current = null;
    if (!current) return;
    if (JSON.stringify(current.before) !== JSON.stringify(docRef.current)) change(docRef.current, current.before);
  };

  useEffect(() => {
    if (!editing) return;
    const area = textEditor.current;
    if (!area) return;
    // 🔑 preventScroll — 글상자가 포스터 밖으로 나가 있으면 브라우저가 포스터 판을 옆으로 밀어
    //    (overflow 숨김 상자도 스크롤된다) 모든 요소가 비껴 보였다.
    area.focus({ preventScroll: true });
    area.setSelectionRange(area.value.length, area.value.length);
  }, [editing]);

  /* ---------- 사진 ---------- */
  const uploadPhoto = async (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/u.test(file.type)) {
      showToast("JPG · PNG · WEBP 그림만 올릴 수 있어요");
      return;
    }
    if (file.size > POSTER_PHOTO_MAX_BYTES) {
      showToast("10MB 보다 큰 그림은 올릴 수 없어요");
      return;
    }
    if (media.photos.length >= POSTER_PHOTO_LIMIT) {
      showToast("사진은 5장까지 올릴 수 있어요");
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadPosterPhoto(workId, file);
      const aspect = await new Promise<number>((resolve) => {
        const image = new Image();
        image.onload = () => resolve(image.naturalWidth / Math.max(1, image.naturalHeight));
        image.onerror = () => resolve(1);
        image.src = URL.createObjectURL(file);
      });
      const nextMedia = { ...media, photos: [...media.photos, { asset_id: uploaded.asset_id, url: uploaded.url }] };
      setMedia(nextMedia);
      add(imageElement({ kind: "photo", asset_id: uploaded.asset_id }, aspect));
      showToast("올렸어요 · 포스터에 넣었어요");
    } catch (caught) {
      showToast(caught instanceof ApiError ? caught.message : "사진을 올리지 못했어요");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (![...event.dataTransfer.types].includes("Files")) return;
    event.preventDefault();
    setTool("image");
    void uploadPhoto(event.dataTransfer.files[0]);
  };

  const reload = async () => {
    const fresh = await refresh();
    if (!fresh) return;
    const next = withoutQr(fresh.doc ?? starterDoc(fresh));
    docRef.current = next;
    setDoc(next);
    setMedia(fresh);
    saver.reset(fresh.revision);
    setSelected(null);
  };

  const leaveTo = async (step: 1 | 3) => {
    finishTextEdit();
    setLeaving(true);
    try {
      await saver.flushNow();
      await goPosterStep(workId, step);
    } catch (caught) {
      showToast(caught instanceof ApiError ? caught.message : "저장하지 못했어요. 다시 눌러 주세요.");
      setLeaving(false);
    }
  };

  const editingElement = editing ? doc.els.find((element) => element.id === editing) as PosterTextElement | undefined : undefined;

  return (
    <StudioShell
      currentStep={2}
      wide
      imageRegenRemaining={state.image_regen_remaining}
      onStepClick={(step) => {
        if (step === 1 || step === 3) void leaveTo(step);
      }}
    >
      <div className="poster-editor-bar">
        <span className={`poster-save-state is-${saveState}`} role="status" aria-live="polite">{SAVE_LABEL[saveState]}</span>
        <div className="poster-editor-bar-actions">
          <button className="button-secondary" type="button" onClick={() => { finishTextEdit(); setDownloadOpen(true); }}>내려받기</button>
          <button className="button-ghost" type="button" disabled={!history.current.canUndo} onClick={undo}>↶ 되돌리기</button>
          <button className="button-ghost" type="button" disabled={!history.current.canRedo} onClick={redo}>↷ 다시</button>
          <button className="button-primary" type="button" disabled={leaving || saveState === "conflict"} onClick={() => void leaveTo(3)}>
            {leaving ? <InlineSpinner label="저장 중..." /> : "다음: 노래 · AR →"}
          </button>
        </div>
      </div>
      {saveState === "conflict" ? (
        <div className="poster-conflict" role="alert">
          <span>다른 곳(다른 탭·다른 컴퓨터)에서 이 포스터를 고쳤어요. 여기서 고친 것은 저장되지 않았어요.</span>
          <button className="button-primary" type="button" onClick={() => void reload()}>새로 불러오기</button>
        </div>
      ) : null}
      <div className="poster-editor">
        <nav className="poster-tools" aria-label="편집 도구">
          {TOOLS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`poster-tool${tool === item.key && !selectedElement ? " is-on" : ""}`}
              aria-pressed={tool === item.key && !selectedElement}
              onClick={() => { finishTextEdit(); setSelected(null); setTool(item.key); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{item.icon}</svg>
              {item.label}
            </button>
          ))}
          <button type="button" className="poster-tool poster-tool-delete" disabled={!selectedElement} onClick={remove}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
            지우기
          </button>
        </nav>

        <div className="poster-stage" ref={stageRef} onDragOver={(event) => { if ([...event.dataTransfer.types].includes("Files")) event.preventDefault(); }} onDrop={onDrop}>
          <div className="poster-stage-top"><span>A3 세로 · 297 × 420mm</span></div>
          {stageSize.width > 0 ? (
            <div
              ref={pageRef}
              className="poster-page poster-page-edit"
              style={{ width: POSTER_PAGE.width * scale, height: POSTER_PAGE.height * scale, background: doc.bg }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {doc.els.map((element) => {
                const isSelected = element.id === selected;
                return (
                  <PosterElementView
                    key={element.id}
                    element={element}
                    scale={scale}
                    layout={layouts.get(element.id)}
                    resolveImage={resolveImage}
                    qrUrl={state.ar_url}
                    className={`poster-edit-el${isSelected ? " is-selected" : ""}${editing === element.id ? " is-editing" : ""}`}
                  >
                    {isSelected && editing !== element.id ? (
                      <>
                        {element.type !== "qr" ? <span className="poster-handle is-rot" data-handle="rot" aria-hidden="true" /> : null}
                        <span className="poster-handle is-se" data-handle="se" aria-hidden="true" />
                      </>
                    ) : null}
                  </PosterElementView>
                );
              })}
              <div
                className="poster-auto-qr-spot"
                aria-hidden="true"
                style={{ left: AUTO_QR.x * scale, top: AUTO_QR.y * scale, width: AUTO_QR.w * scale, height: AUTO_QR.h * scale }}
              >
                <span>QR 자리</span>
              </div>
              {editingElement ? (
                <textarea
                  ref={textEditor}
                  className="poster-text-editor"
                  defaultValue={editingElement.text}
                  aria-label="글 고치기"
                  maxLength={500}
                  onBlur={finishTextEdit}
                  style={{
                    left: editingElement.x * scale,
                    top: editingElement.y * scale,
                    width: editingElement.w * scale,
                    minHeight: (layouts.get(editingElement.id)?.height ?? 10) * scale,
                    transform: editingElement.rot ? `rotate(${editingElement.rot}deg)` : undefined,
                    fontFamily: `"${posterFont(editingElement.font).family}"`,
                    fontWeight: cssWeight(posterFont(editingElement.font), editingElement.weight),
                    fontSize: editingElement.size * MM_PER_PT * scale,
                    lineHeight: `${(layouts.get(editingElement.id)?.lineHeight ?? 5) * scale}px`,
                    color: editingElement.color,
                    textAlign: editingElement.align,
                  }}
                />
              ) : null}
            </div>
          ) : null}
          <p className="poster-stage-foot">빈 곳을 누르면 선택이 풀려요 · Delete 로 지우기 · Ctrl+Z 되돌리기 · 글상자는 두 번 눌러 고쳐요</p>
          {toast ? <div className="poster-toast" role="status">{toast}</div> : null}
        </div>

        <aside className="poster-props" aria-label={selectedElement ? `${ELEMENT_KIND_LABEL[selectedElement.type]} 고치기` : "넣기"}>
          {selectedElement ? (
            <SelectionPanel
              element={selectedElement}
              media={media}
              onChange={(patch) => change(updateElement(docRef.current, selectedElement.id, patch))}
              onLive={(patch) => {
                const updated = updateElement(docRef.current, selectedElement.id, patch);
                docRef.current = updated;
                setDoc(updated);
              }}
              onCommitLive={(before) => change(docRef.current, before)}
              snapshot={() => docRef.current}
              onOrder={(action) => change(reorderElement(docRef.current, selectedElement.id, action))}
              onDuplicate={() => {
                const result = duplicateElement(docRef.current, selectedElement.id);
                if (result.id) {
                  change(result.doc);
                  setSelected(result.id);
                }
              }}
              onRemove={remove}
              onDeselect={() => setSelected(null)}
            />
          ) : (
            <AddPanel
              tool={tool}
              state={media}
              bg={doc.bg}
              uploading={uploading}
              onAddText={(preset) => add(textElement(preset))}
              onAddImage={(source, aspect) => add(imageElement(source, aspect))}
              onAddShape={(kind) => add(shapeElement(kind))}
              onAddSticker={(kind) => add(stickerElement(kind))}
              onBackground={(bg) => change({ ...docRef.current, bg })}
              onUpload={(file) => void uploadPhoto(file)}
              onDraw={() => void leaveTo(1)}
            />
          )}
        </aside>
      </div>
      {downloadOpen ? <PosterDownloadDialog workId={workId} state={media} doc={doc} onClose={() => setDownloadOpen(false)} /> : null}
    </StudioShell>
  );
}

/* ---------- 오른쪽 판: 넣기 ---------- */
function AddPanel({
  tool,
  state,
  bg,
  uploading,
  onAddText,
  onAddImage,
  onAddShape,
  onAddSticker,
  onBackground,
  onUpload,
  onDraw,
}: {
  tool: Tool;
  state: PosterStateResponse;
  bg: string;
  uploading: boolean;
  onAddText: (preset: TextPreset) => void;
  onAddImage: (source: PosterImageSource, aspect: number) => void;
  onAddShape: (kind: PosterShapeKind) => void;
  onAddSticker: (kind: PosterStickerKind) => void;
  onBackground: (bg: string) => void;
  onUpload: (file: File | undefined) => void;
  onDraw: () => void;
}) {
  if (tool === "text") {
    return (
      <>
        <h4>글상자 넣기</h4>
        <p className="poster-props-hint">누르면 포스터 가운데 들어가요. 두 번 눌러 글을 고쳐요.</p>
        <button className="poster-add-text" type="button" style={{ fontFamily: '"Black Han Sans"', fontSize: 24 }} onClick={() => onAddText("title")}>제목 넣기</button>
        <button className="poster-add-text" type="button" style={{ fontFamily: '"Noto Sans KR"', fontWeight: 700, fontSize: 17 }} onClick={() => onAddText("sub")}>부제목 넣기</button>
        <button className="poster-add-text" type="button" style={{ fontFamily: '"Gowun Dodum"', fontSize: 14 }} onClick={() => onAddText("body")}>본문 글 넣기</button>
        <button className="poster-add-text" type="button" style={{ fontFamily: '"Noto Sans KR"', fontSize: 13 }} onClick={() => onAddText("info")}>날짜 · 장소 넣기</button>
      </>
    );
  }
  if (tool === "image") {
    const slotNames = POSTER_SLOT_NAME;
    return (
      <>
        <h4>그림 넣기</h4>
        <p className="poster-props-hint">내가 만든 AI 그림이에요. 누르면 포스터에 들어가요.</p>
        <div className="poster-add-grid">
          {state.cuts.map((cut) => (
            cut.image_url ? (
              <button key={cut.slot} type="button" className="poster-add-image" onClick={() => onAddImage({ kind: "slot", slot: cut.slot }, POSTER_SHAPE_ASPECT[cut.shape])}>
                <img src={cut.image_url} alt="" />
                <span>{slotNames[cut.slot]}</span>
              </button>
            ) : cut.active_job ? (
              <div key={cut.slot} className="poster-add-image is-wait"><span className="poster-spin" aria-hidden="true" /><span>{slotNames[cut.slot]} · 그리는 중</span></div>
            ) : null
          ))}
        </div>
        <p className="poster-props-hint">내가 올린 그림 · 사진 <span>({state.photos.length}/{POSTER_PHOTO_LIMIT})</span></p>
        <label className={`poster-upload${state.photos.length >= POSTER_PHOTO_LIMIT ? " is-full" : ""}`}>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            disabled={uploading || state.photos.length >= POSTER_PHOTO_LIMIT}
            onChange={(event) => { onUpload(event.target.files?.[0]); event.target.value = ""; }}
          />
          <b>{uploading ? "올리는 중..." : state.photos.length >= POSTER_PHOTO_LIMIT ? "5장을 다 올렸어요" : "내 컴퓨터에서 올리기"}</b>
          <span>JPG · PNG · WEBP · 한 장 10MB까지<br />여기로 끌어다 놓아도 돼요</span>
        </label>
        {state.photos.length ? (
          <div className="poster-add-grid is-three">
            {state.photos.map((photo) => (
              <button key={photo.asset_id} type="button" className="poster-add-image" onClick={() => onAddImage({ kind: "photo", asset_id: photo.asset_id }, 1)}>
                <img src={photo.url} alt="" />
              </button>
            ))}
          </div>
        ) : null}
        <div className="poster-upload-warn">친구 얼굴이 나온 사진은 <b>친구에게 먼저 물어보고</b> 올려요. 인터넷에서 찾은 그림은 다른 사람의 작품일 수 있어요. 올린 사진은 AI 로 보내지 않아요.</div>
        <button className="button-secondary full-width" type="button" onClick={onDraw}>그림 더 만들러 가기</button>
      </>
    );
  }
  if (tool === "shape") {
    return (
      <>
        <h4>도형 넣기</h4>
        <p className="poster-props-hint">제목 띠, 구분선, 날짜 상자를 만들 때 써요.</p>
        <div className="poster-add-grid is-three">
          {(Object.keys(SHAPE_NAMES) as PosterShapeKind[]).map((kind) => (
            <button key={kind} type="button" className="poster-add-shape" onClick={() => onAddShape(kind)}>
              <ShapeIcon kind={kind} />
              <span>{SHAPE_NAMES[kind]}</span>
            </button>
          ))}
        </div>
      </>
    );
  }
  if (tool === "sticker") {
    return (
      <>
        <h4>스티커 넣기</h4>
        <p className="poster-props-hint">누르면 포스터에 붙어요. 색은 붙인 뒤 바꿀 수 있어요.</p>
        <div className="poster-add-grid is-four">
          {(Object.keys(STICKER_PATHS) as PosterStickerKind[]).map((kind) => (
            <button key={kind} type="button" className="poster-add-shape" onClick={() => onAddSticker(kind)}>
              <svg viewBox="0 0 100 100" width="34" height="34" fill="#e0703a" aria-hidden="true"><path d={STICKER_PATHS[kind]} /></svg>
              <span>{STICKER_NAMES[kind]}</span>
            </button>
          ))}
        </div>
      </>
    );
  }
  return (
    <>
      <h4>배경색</h4>
      <p className="poster-props-hint">포스터 바탕 전체의 색이에요.</p>
      <div className="poster-bg-grid">
        {POSTER_BACKGROUNDS.map((value) => (
          <button
            key={value}
            type="button"
            className={`poster-bg-swatch${value === bg ? " is-on" : ""}`}
            style={{ background: value }}
            aria-label={`배경 ${value}`}
            aria-pressed={value === bg}
            onClick={() => onBackground(value)}
          />
        ))}
      </div>
      <p className="poster-props-hint">위 두 줄은 단색, 마지막 줄은 그라데이션이에요.</p>
    </>
  );
}

function ShapeIcon({ kind }: { kind: PosterShapeKind }) {
  if (kind === "circle") return <svg width="40" height="34" viewBox="0 0 40 34" aria-hidden="true"><ellipse cx="20" cy="17" rx="17" ry="15" fill="#57534e" /></svg>;
  if (kind === "triangle") return <svg width="40" height="34" viewBox="0 0 40 34" aria-hidden="true"><path d="M20 2 38 32H2z" fill="#57534e" /></svg>;
  if (kind === "line") return <svg width="40" height="34" viewBox="0 0 40 34" aria-hidden="true"><rect x="2" y="15" width="36" height="4" fill="#57534e" /></svg>;
  const radius = kind === "pill" ? 9 : kind === "round" ? 6 : 0;
  const height = kind === "pill" ? 18 : 30;
  return <svg width="40" height="34" viewBox="0 0 40 34" aria-hidden="true"><rect x="2" y={(34 - height) / 2} width="36" height={height} rx={radius} fill="#57534e" /></svg>;
}

/* ---------- 오른쪽 판: 고치기 ---------- */
function Swatches({ values, current, onPick, label }: { values: readonly string[]; current: string; onPick: (value: string) => void; label: string }) {
  return (
    <div className="poster-swatches" role="radiogroup" aria-label={label}>
      {values.map((value) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={value === current}
          aria-label={value === "none" ? "없음" : value === "transparent" ? "투명" : value}
          className={`poster-swatch${value === current ? " is-on" : ""}${value === "none" || value === "transparent" ? " is-none" : ""}`}
          style={value === "none" || value === "transparent" ? undefined : { background: value }}
          onClick={() => onPick(value)}
        />
      ))}
    </div>
  );
}

function Segments<T extends string | number>({ options, current, onPick, disabled }: {
  options: Array<[T, string]>;
  current: T;
  onPick: (value: T) => void;
  disabled?: (value: T) => boolean;
}) {
  return (
    <div className="poster-seg">
      {options.map(([value, label]) => (
        <button key={String(value)} type="button" className={value === current ? "is-on" : ""} aria-pressed={value === current} disabled={disabled?.(value)} onClick={() => onPick(value)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Slider({ label, min, max, step, value, unit, onLive, onCommit }: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit: string;
  onLive: (value: number) => void;
  onCommit: () => void;
}) {
  return (
    <div className="poster-slider">
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onLive(Number(event.target.value))}
        onPointerUp={onCommit}
        onKeyUp={onCommit}
      />
      <span>{Math.round(value * 100) / 100}{unit}</span>
    </div>
  );
}

function SelectionPanel({
  element,
  media,
  onChange,
  onLive,
  onCommitLive,
  snapshot,
  onOrder,
  onDuplicate,
  onRemove,
  onDeselect,
}: {
  element: PosterElement;
  media: PosterStateResponse;
  onChange: (patch: Partial<PosterElement>) => void;
  onLive: (patch: Partial<PosterElement>) => void;
  onCommitLive: (before: PosterDoc) => void;
  snapshot: () => PosterDoc;
  onOrder: (action: OrderAction) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onDeselect: () => void;
}) {
  const liveBefore = useRef<PosterDoc | null>(null);
  const live = (patch: Partial<PosterElement>) => {
    liveBefore.current ??= snapshot();
    onLive(patch);
  };
  const commit = () => {
    if (liveBefore.current) onCommitLive(liveBefore.current);
    liveBefore.current = null;
  };
  const head = (
    <div className="poster-sel-head">
      <h4>{ELEMENT_KIND_LABEL[element.type]} 고침</h4>
      <button className="button-ghost" type="button" onClick={onDeselect}>선택 풀기</button>
    </div>
  );
  if (element.type === "qr") return head;
  const common = (
    <>
      <div className="poster-prow">
        <label>회전</label>
        <Slider label="회전" min={-180} max={180} step={1} value={Math.round(element.rot)} unit="°" onLive={(rot) => live({ rot })} onCommit={commit} />
      </div>
      <div className="poster-prow">
        <label>순서</label>
        <div className="poster-seg">
          <button type="button" onClick={() => onOrder("front")}>맨 앞으로</button>
          <button type="button" onClick={() => onOrder("forward")}>한 칸 앞</button>
          <button type="button" onClick={() => onOrder("back")}>맨 뒤로</button>
        </div>
      </div>
      <div className="poster-prow poster-prow-actions">
        <button className="button-secondary" type="button" onClick={onDuplicate}>복제</button>
        <button className="button-secondary text-danger" type="button" onClick={onRemove}>지우기</button>
      </div>
    </>
  );
  if (element.type === "text") {
    const font = posterFont(element.font);
    const missing = missingGlyphs(element.font, element.text);
    return (
      <>
        {head}
        <div className="poster-prow">
          <label>글꼴 <small>(Canva 에도 있는 글꼴만)</small></label>
          <div className="poster-fonts">
            {POSTER_FONTS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`poster-font${option.key === element.font ? " is-on" : ""}`}
                aria-pressed={option.key === element.font}
                style={{ fontFamily: `"${option.family}"`, fontWeight: option.regularWeight }}
                onClick={() => onChange({ font: option.key, weight: option.bold ? element.weight : 400 } as Partial<PosterTextElement>)}
              >
                {option.label} <small>{option.role}</small>
              </button>
            ))}
          </div>
          {missing.length ? (
            <p className="poster-glyph-warn" role="alert">이 글꼴에는 «{missing.join(" ")}» 글자가 없어요. 인쇄하면 빈칸이 돼요 — 다른 글꼴을 쓰거나 글자를 바꿔 주세요.</p>
          ) : null}
        </div>
        <div className="poster-prow">
          <label>크기</label>
          <Slider label="글자 크기" min={8} max={200} step={1} value={element.size} unit="pt" onLive={(size) => live({ size } as Partial<PosterTextElement>)} onCommit={commit} />
        </div>
        <div className="poster-prow">
          <label>글자색</label>
          <Swatches label="글자색" values={POSTER_COLORS} current={element.color} onPick={(color) => onChange({ color } as Partial<PosterTextElement>)} />
        </div>
        <div className="poster-prow">
          <label>굵기 · 정렬</label>
          <div className="poster-prow-pair">
            <Segments<400 | 700>
              options={[[400, "보통"], [700, "굵게"]]}
              current={element.weight}
              disabled={(value) => value === 700 && !font.bold}
              onPick={(weight) => onChange({ weight } as Partial<PosterTextElement>)}
            />
            <Segments<"left" | "center" | "right">
              options={[["left", "왼쪽"], ["center", "가운데"], ["right", "오른쪽"]]}
              current={element.align}
              onPick={(align) => onChange({ align } as Partial<PosterTextElement>)}
            />
          </div>
          {!font.bold ? <p className="poster-props-hint">이 글꼴은 굵은 글씨가 없어요.</p> : null}
        </div>
        {common}
      </>
    );
  }
  if (element.type === "image") {
    return (
      <>
        {head}
        {element.src.kind === "photo" ? <div className="poster-tag poster-tag-block">내가 올린 사진</div> : null}
        <div className="poster-prow">
          <label>모양</label>
          <Segments<"rect" | "round" | "circle"> options={[["rect", "네모"], ["round", "둥근 네모"], ["circle", "원"]]} current={element.shape} onPick={(shape) => onChange({ shape } as Partial<PosterElement>)} />
        </div>
        <div className="poster-prow">
          <label>테두리</label>
          <Swatches label="테두리" values={["none", "#fbf6ee", "#ffffff", "#1c1917", "#17644f", "#e0703a"]} current={element.border} onPick={(border) => onChange({ border } as Partial<PosterElement>)} />
        </div>
        <div className="poster-prow">
          <label>투명도</label>
          <Slider label="투명도" min={0.2} max={1} step={0.05} value={element.opacity} unit="" onLive={(opacity) => live({ opacity })} onCommit={commit} />
        </div>
        <div className="poster-prow">
          <label>그림 바꾸기</label>
          <div className="poster-add-grid is-four">
            {media.cuts.filter((cut) => cut.image_url).map((cut) => (
              <button
                key={cut.slot}
                type="button"
                className={`poster-add-image${element.src.kind === "slot" && element.src.slot === cut.slot ? " is-on" : ""}`}
                onClick={() => onChange({ src: { kind: "slot", slot: cut.slot } } as Partial<PosterElement>)}
              ><img src={cut.image_url!} alt={`${cut.slot}번 칸 그림`} /></button>
            ))}
            {media.photos.map((photo) => (
              <button
                key={photo.asset_id}
                type="button"
                className={`poster-add-image${element.src.kind === "photo" && element.src.asset_id === photo.asset_id ? " is-on" : ""}`}
                onClick={() => onChange({ src: { kind: "photo", asset_id: photo.asset_id } } as Partial<PosterElement>)}
              ><img src={photo.url} alt="올린 사진" /></button>
            ))}
          </div>
        </div>
        {common}
      </>
    );
  }
  if (element.type === "shape") {
    return (
      <>
        {head}
        <div className="poster-prow">
          <label>모양</label>
          <Segments<PosterShapeKind> options={[["rect", "사각"], ["round", "둥근"], ["circle", "원"], ["triangle", "삼각"], ["pill", "알약"]]} current={element.kind} onPick={(kind) => onChange({ kind } as Partial<PosterElement>)} />
        </div>
        <div className="poster-prow">
          <label>채우기</label>
          <Swatches label="채우기" values={[...POSTER_COLORS, "transparent"]} current={element.fill} onPick={(fill) => onChange({ fill } as Partial<PosterElement>)} />
        </div>
        <div className="poster-prow">
          <label>테두리</label>
          <Swatches label="테두리" values={["none", ...POSTER_COLORS]} current={element.stroke} onPick={(stroke) => onChange({ stroke } as Partial<PosterElement>)} />
        </div>
        <div className="poster-prow">
          <label>투명도</label>
          <Slider label="투명도" min={0.1} max={1} step={0.05} value={element.opacity} unit="" onLive={(opacity) => live({ opacity })} onCommit={commit} />
        </div>
        {common}
      </>
    );
  }
  return (
    <>
      {head}
      <div className="poster-prow">
        <label>색</label>
        <Swatches label="스티커 색" values={POSTER_COLORS} current={element.color} onPick={(color) => onChange({ color } as Partial<PosterElement>)} />
      </div>
      <div className="poster-prow">
        <label>바꾸기</label>
        <div className="poster-add-grid is-four">
          {(Object.keys(STICKER_PATHS) as PosterStickerKind[]).map((kind) => (
            <button key={kind} type="button" className={`poster-add-shape${kind === element.kind ? " is-on" : ""}`} aria-label={STICKER_NAMES[kind]} onClick={() => onChange({ kind } as Partial<PosterElement>)}>
              <svg viewBox="0 0 100 100" width="26" height="26" fill={element.color} aria-hidden="true"><path d={STICKER_PATHS[kind]} /></svg>
            </button>
          ))}
        </div>
      </div>
      <div className="poster-prow">
        <label>투명도</label>
        <Slider label="투명도" min={0.2} max={1} step={0.05} value={element.opacity} unit="" onLive={(opacity) => live({ opacity })} onCommit={commit} />
      </div>
      {common}
    </>
  );
}
